import React, { createContext, useContext, useEffect, useState } from "react";
import { STORAGE_KEYS } from "@/config/constants";
import { storage } from "@/shared/utils/storage";
import { injectLogout } from "@/shared/services/apiClient";
import { changeLanguageAndPersist } from "@/i18n";
import type { UserProfile, AuthContextType } from "@/features/auth/types/auth";
import { refreshUserProfile } from "@/features/auth/services/authService";
import { accessService } from "@/features/admin/services/accessService";

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  userToken: null,
  streamToken: null,
  userData: null,
  roles: [],
  login: () => {},
  logout: () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userToken, setUserToken] = useState<string | null>(null);
  const [streamToken, setStreamToken] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserProfile | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const storedToken       = storage.get(STORAGE_KEYS.USER_TOKEN);
    const storedStreamToken = storage.get(STORAGE_KEYS.STREAM_TOKEN);
    const storedUser        = storage.getJSON<UserProfile>(STORAGE_KEYS.USER_DATA);
    const storedRoles       = storage.getJSON<string[]>(STORAGE_KEYS.ROLES) ?? [];

    if (storedToken && storedUser) {
      setUserToken(storedToken);
      setStreamToken(storedStreamToken);
      setUserData(storedUser);
      setRoles(storedRoles);
      setIsAuthenticated(true);
      if (storedUser.preferred_language) {
        changeLanguageAndPersist(storedUser.preferred_language);
      }

      // Refresh user profile + roles from the server on every page load so that
      // changes to v4.business_unit_tbl and v4.user_roles are picked up immediately.
      const userId = String(storedUser.id);
      Promise.all([
        refreshUserProfile().catch((err) => {
          console.error("[AuthContext] Failed to refresh user profile:", err);
          return null;
        }),
        accessService.getUserRoles(userId).catch((err) => {
          console.error("[AuthContext] Failed to refresh user roles:", err);
          return null;
        }),
      ]).then(([freshUser, freshRoles]) => {
        if (freshUser) {
          storage.setJSON(STORAGE_KEYS.USER_DATA, freshUser);
          setUserData(freshUser);
          if (freshUser.preferred_language) {
            changeLanguageAndPersist(freshUser.preferred_language);
          }
        }
        if (freshRoles) {
          storage.setJSON(STORAGE_KEYS.ROLES, freshRoles);
          setRoles(freshRoles);
        }
      });
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || userData?.userType?.toLowerCase() !== "souser") return;
    let active = true;
    const refresh = async () => {
      try {
        const fresh = await refreshUserProfile();
        if (!active) return;
        storage.setJSON(STORAGE_KEYS.USER_DATA, fresh);
        setUserData(fresh);
      } catch { /* API still enforces live permissions while offline. */ }
    };
    void refresh();
    window.addEventListener("focus", refresh);
    const timer = window.setInterval(refresh, 60_000);
    return () => { active = false; window.removeEventListener("focus", refresh); window.clearInterval(timer); };
  }, [isAuthenticated, userData?.id, userData?.userType]);

  const login = (
    token: string,
    streamTkn: string,
    user: UserProfile,
    userRoles: string[] = [],
  ) => {
    storage.set(STORAGE_KEYS.USER_TOKEN, token);
    storage.set(STORAGE_KEYS.SECURE_TOKEN, token);
    storage.set(STORAGE_KEYS.STREAM_TOKEN, streamTkn);
    storage.setJSON(STORAGE_KEYS.USER_DATA, user);
    storage.setJSON(STORAGE_KEYS.ROLES, userRoles);

    setUserToken(token);
    setStreamToken(streamTkn);
    setUserData(user);
    setRoles(userRoles);
    setIsAuthenticated(true);
    if (user.preferred_language) {
      changeLanguageAndPersist(user.preferred_language);
    }
  };

  const logout = () => {
    storage.remove(STORAGE_KEYS.USER_TOKEN);
    storage.remove(STORAGE_KEYS.STREAM_TOKEN);
    storage.remove(STORAGE_KEYS.USER_DATA);
    storage.remove(STORAGE_KEYS.SECURE_TOKEN);
    storage.remove(STORAGE_KEYS.ROLES);

    setUserToken(null);
    setStreamToken(null);
    setUserData(null);
    setRoles([]);
    setIsAuthenticated(false);
  };

  // Inject logout into API client for 401 handling
  useEffect(() => {
    injectLogout(logout);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        userToken,
        streamToken,
        userData,
        roles,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
